import Button from '@/components/common/Button'
import { H3, H4 } from '@/components/common/Typo'
import IconDelivery from '@/components/icons/IconDelivery'
import IconPickup from '@/components/icons/IconPickup'
import { useModal } from '@/contexts/ModalContext'
import { useOrderSetup } from '@/contexts/OrderSetupContext'
import { MODAL_TYPES } from '@/libs/config/modal'
import { selectDetailMenuSite } from '@/redux/meta'
import { useTranslate } from '@tolgee/react'
import {
  Card,
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from 'oddle-shop-monorepo.design-system'
import { useSelector } from 'react-redux'
import { useGetAddressBookQuery } from '@/services/local'
import useAuth from '@/hooks/useAuth'

const PickupDeliverySelectionModal = ({
  modal,
}: {
  modal: {
    show: boolean
    onClose: () => void
    onClosed: () => void
  }
}) => {
  const { t } = useTranslate()
  const { hasPickup, hasDelivery } = useSelector(selectDetailMenuSite) || {}
  const { data: user } = useAuth()
  useGetAddressBookQuery(undefined, {
    skip: !user?.data?.payload?.id,
  })

  const { isLoading, setOrderMethod, forceFinishOrderSetup } = useOrderSetup()
  const { closeModal, openModal, modalProps } = useModal()

  const syncMethod = async ({ method }: { method: 'pickup' | 'delivery' }) => {
    await setOrderMethod(method)
    if (typeof modalProps.onAfterClose === 'function') {
      modalProps.onAfterClose()
    }
    openModal(
      method === 'pickup'
        ? MODAL_TYPES.PICKUP_STORE_SELECTION
        : MODAL_TYPES.DELIVERY_ADDRESS_SELECTION,
      modalProps
    )
  }

  const openPickUpModal = () => syncMethod({ method: 'pickup' })
  const openDeliveryModal = () => syncMethod({ method: 'delivery' })
  const onSkipMenuClicked = () => {
    closeModal()
    forceFinishOrderSetup()
  }

  return (
    <Dialog
      open={modal.show}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          modal.onClose()
          if (typeof modalProps.onAfterClose === 'function') {
            modalProps.onAfterClose()
          }
        }
      }}
    >
      <DialogPortal>
        <DialogContent
          aria-describedby={undefined}
          onPointerDownOutside={(e: Event) => {
            e.preventDefault()
          }}
          onInteractOutside={(e: Event) => {
            e.preventDefault()
          }}
          className={cn(
            'fixed h-full w-full max-w-full p-0 shadow-xl sm:h-fit sm:max-w-lg'
          )}
          onClickClose={onSkipMenuClicked}
        >
          <DialogHeader className="text-headings bg-container max-h-[60px] justify-center p-4 shadow-sm sm:rounded-t-2xl">
            <DialogTitle asChild className="text-center">
              <H4>{t('Select your dining preference')}</H4>
            </DialogTitle>
          </DialogHeader>
          <div className="text-body bg-background p-4 sm:rounded-b-2xl">
            <div className="relative">
              <div className="flex w-full flex-row justify-center">
                {hasPickup && (
                  <Card
                    role="button"
                    onClick={openPickUpModal}
                    className={cn(
                      'bg-container mb-0 flex w-[157px] cursor-pointer flex-col items-center justify-center gap-0 rounded-lg md:w-[190px]',
                      hasDelivery ? 'mr-4' : 'mr-0'
                    )}
                  >
                    <H3 className="m-0">{t('Pickup')}</H3>
                    <div className="text-button mb-[40px] mt-[20px] shrink-0">
                      <IconPickup className="!h-[48px] !w-[48px]" />
                    </div>
                    <Button className="mt-auto" disabled={isLoading}>
                      {t('Select')}
                    </Button>
                  </Card>
                )}
                {hasDelivery && (
                  <Card
                    role="button"
                    onClick={openDeliveryModal}
                    className={cn(
                      'bg-container mb-0 flex w-[157px] cursor-pointer flex-col items-center justify-center gap-0 rounded-lg md:w-[190px]',
                      hasPickup ? 'mr-4' : 'mr-0'
                    )}
                  >
                    <H3 className="m-0">{t('Delivery')}</H3>
                    <div className="mb-[40px] mt-[20px] shrink-0">
                      <IconDelivery className="text-button !h-[48px] !w-[48px]" />
                    </div>
                    <Button className="mt-auto" disabled={isLoading}>
                      {t('Select')}
                    </Button>
                  </Card>
                )}
              </div>
              <div className="mb-2 mt-8 flex justify-center">
                <Button
                  variant="link"
                  onClick={async () => {
                    onSkipMenuClicked()
                    if (typeof modalProps.onFinished === 'function') {
                      await modalProps.onFinished()
                    }
                    if (typeof modalProps.onAfterClose === 'function') {
                      await modalProps.onAfterClose()
                    }
                  }}
                  className="text-section-headings font-normal underline"
                  disabled={isLoading}
                >
                  {t(`I'm just browsing`)}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default PickupDeliverySelectionModal
